/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Input, OnChanges, OnInit, ViewChild, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, FormBuilder, NgForm, FormArray, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IFieldConfig, FieldTypes } from '../../../../models';

@Component({
  exportAs: 'appForm',
  selector: 'lib-form',
  templateUrl: './form.component.html',
  imports: [FormsModule, ReactiveFormsModule],
})
export class AppFormComponent implements OnInit, OnChanges {
  private readonly fb = inject(FormBuilder);

  @ViewChild('formRef', { static: true }) ngForm!: NgForm;
  @Input() model: any;
  @Input() fullWidth!: boolean;
  form!: UntypedFormGroup;
  _config: IFieldConfig[] = [];

  @Input()
  set config(value: IFieldConfig[]) {
    this._config = value;
  }

  get config(): IFieldConfig[] {
    return this._config.filter((x) => !x.hidden);
  }

  ngOnInit() {
    this.form = this.createGroup();
    if (this.model) {
      this.form.patchValue(this.model);
    }
  }

  ngOnChanges() {
    this.configChanged();
  }

  configChanged() {
    if (this.form) {
      const controls = Object.keys(this.form.controls);
      const visibleConfig = this.visibleConfig.map((item) => item.name);

      controls.filter((control) => visibleConfig.indexOf(control) === -1).forEach((control) => this.form.removeControl(control));

      visibleConfig
        .filter((control) => controls.indexOf(control) === -1)
        .forEach((name) => {
          const config = this.config.find((control) => control.name === name);
          if (config) {
            this.form.addControl(name, this.createControl(config));
          }
        });
    }
  }

  get controls() {
    return this.form.controls;
  }

  get rawValue() {
    return this.form.getRawValue();
  }

  get changes() {
    return this.form.valueChanges;
  }

  get valid() {
    return this.form.valid;
  }

  get value() {
    return this.form.value;
  }

  get submitted() {
    return this.ngForm.submitted;
  }

  private get visibleConfig() {
    return this.config.filter(({ type, hidden }) => type !== FieldTypes.Button && type !== FieldTypes.ButtonGroup && !hidden);
  }

  createGroup() {
    const group = this.fb.group({});
    this.visibleConfig.forEach((control) => group.addControl(control.name, this.createControl(control)));
    return group;
  }

  createControl(config: IFieldConfig): UntypedFormControl | FormArray {
    const { disabled, validation, value, options } = config;
    if (config.type === FieldTypes.CheckboxList) {
      return this.fb.array(
        (options || []).map((item) => {
          return this.fb.control(item.selected);
        }),
        config.validation ? config.validation[0] : null,
      );
    }
    return this.fb.control({ disabled, value }, validation);
  }

  setDisabled(name: string, disable: boolean) {
    if (this.form.controls[name]) {
      const method = disable ? 'disable' : 'enable';
      this.form.controls[name][method]();
      return;
    }

    this.config = this.config.map((item) => {
      if (item.name === name) {
        item.disabled = disable;
      }
      return item;
    });
  }

  setValue(name: string, value: any) {
    this.form.controls[name].setValue(value, { emitEvent: true });
  }

  patchForm(model: { [key: string]: any }) {
    this.form.patchValue(model);
  }

  reset() {
    this.ngForm.resetForm();
  }

  validate() {
    Object.keys(this.form.controls).forEach((field) => {
      const control = this.form.get(field);
      control?.markAsTouched({ onlySelf: true });
    });
  }

  showControl(name: string, show: boolean) {
    const config = this._config.find((control) => control.name === name);
    if (!config) {
      return;
    }
    config.hidden = !show;
    if (show) {
      this.form.addControl(name, this.createControl(config));
    } else {
      this.form.removeControl(name);
    }
  }
}
